import Viewport from './InteractiveGraphic.Viewport';

// State and navigation only. Animation adapters can subscribe to the DOM events.
export default class InteractiveGraphic {
    static instances = new WeakMap();

    static init(root = document) {
        const mount = () => root.querySelectorAll('.interactive-graphic').forEach(element => {
            if (element.dataset.editable === 'true') return;
            if (!this.instances.has(element)) this.instances.set(element, new this(element));
        });
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, {once: true});
        else mount();
    }

    constructor(element) {
        this.element = element;
        this.abort = new AbortController();
        // Bubbles that open a panel; the server made them buttons.
        this.bubbles = [...element.querySelectorAll('[data-bubble-id][data-panel]')];
        this.bubbles.forEach(bubble => this.measure(bubble));
        this.panels = [...element.querySelectorAll('.interactive-graphic__panel')];
        this.steps = this.panels.filter(panel => this.bubbles.some(bubble => bubble.dataset.panel === panel.id));
        this.counter = element.querySelector('.interactive-graphic__counter');
        this.navigation = element.querySelector('.interactive-graphic__navigation');
        this.active = -1;
        this.dots = this.steps.map(panel => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'interactive-graphic__dot';
            dot.dataset.panel = panel.id;
            dot.setAttribute('aria-label', panel.querySelector('.interactive-graphic__panel-title').textContent.trim());
            dot.setAttribute('aria-controls', panel.id);
            element.querySelector('.interactive-graphic__dots').append(dot);
            return dot;
        });
        // A bubble whose panel is not on the page opens nothing.
        this.bubbles = this.bubbles.filter(bubble => {
            if (this.steps.some(panel => panel.id === bubble.dataset.panel)) return true;
            ['role', 'tabindex', 'aria-controls', 'data-panel'].forEach(name => bubble.removeAttribute(name));
            this.plusOf(bubble)?.remove();
            return false;
        });
        this.viewport = new Viewport(element);
        this.plus = element.querySelector('.interactive-graphic__plus-disc');
        this.resize = new ResizeObserver(() => this.sizePlus());
        if (this.plus) this.resize.observe(this.plus.ownerSVGElement);
        const options = {signal: this.abort.signal};
        element.addEventListener('click', event => {
            if (this.viewport.suppressClick) return;
            const target = event.target.closest('[data-bubble-id][data-panel], button');
            if (!target || target.disabled) return;
            if (target.dataset.panel) this.select(this.steps.findIndex(panel => panel.id === target.dataset.panel), target);
            else if (target.matches('.interactive-graphic__previous')) this.step(-1);
            else if (target.matches('.interactive-graphic__next')) this.step(1);
        }, options);
        element.addEventListener('keydown', event => {
            // SVG buttons have no native activation.
            const bubble = event.target.closest('[data-bubble-id][data-panel]');
            if (bubble && ['Enter', ' '].includes(event.key)) {
                event.preventDefault();
                this.select(this.steps.findIndex(panel => panel.id === bubble.dataset.panel), bubble);
                return;
            }
            if (event.target.closest('input, textarea, select, [contenteditable="true"]') || event.altKey || event.ctrlKey || event.metaKey) return;
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || !this.steps.length) return;
            event.preventDefault();
            const sourceIsBubble = Boolean(bubble);
            const sourceIsDot = event.target.closest('.interactive-graphic__dot');
            if (event.key === 'Home') this.select(0);
            else if (event.key === 'End') this.select(this.steps.length - 1);
            else this.step(event.key === 'ArrowRight' ? 1 : -1);
            if (sourceIsBubble) this.bubbles.find(bubble => bubble.dataset.panel === this.steps[this.active].id)?.focus({preventScroll: true});
            if (sourceIsDot) this.dots[this.active].focus({preventScroll: true});
        }, options);
        const panels = element.querySelector('.interactive-graphic__panels');
        let swipe;
        panels.addEventListener('pointerdown', event => {
            if (!event.isPrimary || event.target.closest('a,button,input,textarea,select')) return;
            swipe = {id: event.pointerId, x: event.clientX, y: event.clientY};
        }, options);
        panels.addEventListener('pointerup', event => {
            if (!swipe || swipe.id !== event.pointerId) return;
            const dx = event.clientX - swipe.x, dy = event.clientY - swipe.y;
            if (this.viewport.isMobile && Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) this.step(dx < 0 ? 1 : -1);
            swipe = null;
        }, options);
        panels.addEventListener('pointercancel', () => { swipe = null; }, options);
        this.navigation.hidden = this.steps.length < 2;
        this.panels.forEach(panel => { panel.hidden = true; });
        if (this.steps.length) this.select(Math.max(0, this.steps.findIndex(panel => panel.id === element.dataset.initialPanel)));
        else element.classList.add('is-empty');
        this.observer = new IntersectionObserver(entries => entries.forEach(entry => {
            element.classList.toggle('is-in-view', entry.isIntersecting);
            this.emit('visibility', {isVisible: entry.isIntersecting});
        }), {threshold: 0.15});
        this.observer.observe(element);
        element.classList.add('is-ready');
    }

    select(index, source) {
        if (index < 0 || index >= this.steps.length) return;
        this.active = index;
        const panel = this.steps[index];
        const group = this.bubbles.filter(bubble => bubble.dataset.panel === panel.id);
        this.panels.forEach(item => { item.hidden = item !== panel; });
        this.bubbles.forEach(bubble => {
            const active = group.includes(bubble);
            bubble.closest('.interactive-graphic__bubble')?.classList.toggle('is-active', active);
            this.plusOf(bubble)?.classList.toggle('is-active', active);
            bubble.setAttribute('aria-expanded', String(active));
            bubble.setAttribute('aria-pressed', String(active));
        });
        this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
        this.counter.textContent = `${index + 1} / ${this.steps.length}`;
        this.viewport.focus(group.includes(source) ? [source] : group);
        this.emit('change', {panelId: panel.id, index, bubbles: group});
    }

    // The "+" sits on a layer of its own above the artwork, not inside the bubble's group.
    plusOf(bubble) {
        return this.element.querySelector(`[data-plus-for="${bubble.dataset.bubbleId}"]`);
    }

    // Centre and radius of the bubble in percent of the illustration, for the camera. They live
    // only here, read from the SVG, which fills the canvas at the proportions of its viewBox.
    measure(bubble) {
        const box = bubble.ownerSVGElement?.viewBox?.baseVal;
        if (!box?.width || !box?.height) return;
        bubble.dataset.x = ((bubble.cx.baseVal.value - box.x) / box.width * 100).toFixed(3);
        bubble.dataset.y = ((bubble.cy.baseVal.value - box.y) / box.height * 100).toFixed(3);
        const r = bubble.r?.baseVal.value ?? Math.max(bubble.rx.baseVal.value, bubble.ry.baseVal.value);
        bubble.dataset.r = (r / box.width * 100).toFixed(3);
    }

    // The SVG "+" scales with the illustration; keep its disc at the 32px of the former marker.
    sizePlus() {
        const svg = this.plus.ownerSVGElement;
        const box = svg.viewBox.baseVal;
        if (!box.width || !svg.clientWidth) return;
        const rendered = this.plus.r.baseVal.value * Math.min(svg.clientWidth / box.width, svg.clientHeight / box.height);
        this.element.style.setProperty('--plus-scale', (16 / rendered).toFixed(3));
    }

    step(direction) {
        if (this.steps.length) this.select((this.active + direction + this.steps.length) % this.steps.length);
    }

    emit(name, detail) {
        this.element.dispatchEvent(new CustomEvent(`interactivegraphic:${name}`, {bubbles: true, detail}));
    }

    destroy() {
        this.emit('destroy', {});
        this.abort.abort();
        this.observer.disconnect();
        this.resize.disconnect();
        this.element.style.removeProperty('--plus-scale');
        this.viewport.destroy();
        this.dots.forEach(dot => dot.remove());
        this.panels.forEach(panel => { panel.hidden = false; });
        this.bubbles.forEach(bubble => {
            bubble.closest('.interactive-graphic__bubble')?.classList.remove('is-active');
            this.plusOf(bubble)?.classList.remove('is-active');
            bubble.removeAttribute('aria-expanded');
            bubble.removeAttribute('aria-pressed');
        });
        this.navigation.hidden = true;
        this.element.classList.remove('is-ready', 'is-in-view', 'is-empty');
        InteractiveGraphic.instances.delete(this.element);
    }
}
